import { Info } from "@/Component/Info";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { Space } from "antd";
import { Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";
import { useSearchParams } from "react-router";
import { ReceptionCenterUrlContext } from "./ReceptionCenterUrlContext";

export const useReceptionCenter = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getLocalizedText, getLocalizedDate } = useLocalizedText();
    const listUrlDefaults = useContext(ReceptionCenterUrlContext);

    const sourceFromUrl = searchParams.get("source");
    const currentSource = useMemo(
        () =>
            listUrlDefaults?.activeSource ||
            sourceFromUrl ||
            listUrlDefaults?.defaultSource ||
            "from_ports",
        [sourceFromUrl, listUrlDefaults?.activeSource, listUrlDefaults?.defaultSource]
    );
    const currentDate = searchParams.get("date") || "today";

    const isFromPorts = currentSource === "from_ports";
    const isBetweenCities = currentSource === "between_cities";

    const commonLabelValues = [
        {
            key: "bag_no",
            label: t("BAG_NUMBER"),
        },
        {
            key: "manifest_trip_date",
            label: t("DEPARTURE_TIME"),
        },
        {
            key: "transport_company",
            label: t("TRANSPORT_COMPANY"),
            render: (value) => getLocalizedText(
                value?.name_ar,
                value?.name_en
            ) || "-"
        },
        {
            key: "bus",
            label: t("BUS_NUMBER"),
            render: (value) =>
                value?.bus_number || "-",
        },
        {
            key: "mission",
            label: t("ORGANIZER"),
            render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
        },
        {
            key: "company",
            label: t("AFFILIATED_ORGANIZER"),
            render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
        },
        {
            key: "driver_name",
            label: t("DRIVER_NAME"),
            render: (_, row) => row?.driver?.name || "-",
        },
        {
            key: "driver_mobile",
            label: t("UPCOMING_COL_MOBILE"),
            render: (_, row) => row?.driver?.mobile_number || "-",
        },
        {
            key: "passport_count",
            label: t("PASSPORTS_COUNT"),
        },
    ];

    const commonFilterFields = [
        {
            name: "from_date",
            label: t("FROM_DATE"),
            type: "date",
        },
        {
            name: "to_date",
            label: t("TO_DATE"),
            type: "date",
        },
        {
            name: "bus_id",
            label: t("BUS_NUMBER"),
            type: "select",
            optionKey: "buses",
            options: (bus) => ({
                value: bus.id,
                label: bus.bus_number || bus.plate_no || String(bus.id),
            }),
        },
        {
            name: "mission_id",
            label: t("ORGANIZER"),
            type: "select",
            optionKey: "missions",
            options: (m) => ({
                value: m.id,
                label: getLocalizedText(m?.name_ar, m?.name_en) || m?.name,
            }),
        },
        {
            name: "company_id",
            label: t("AFFILIATED_ORGANIZER"),
            type: "select",
            optionKey: "companies",
            options: (c) => ({
                value: c.id,
                label: getLocalizedText(c?.name_ar, c?.name_en) || c?.name,
            }),
        },
        {
            name: "transportation_company_id",
            label: t("TRANSPORT_COMPANY"),
            type: "select",
            optionKey: "buses",
            options: (bus) => ({
                value: bus.company_id,
                label: [bus.company_id, bus.company_name].filter(Boolean).join(" - "),
            }),
        },
        {
            name: "contract_id",
            label: t("HOUSING_CONTRACT"),
            type: "select",
            optionKey: "contracts",
            options: (c) => ({
                value: c.id,
                label: [c?.id, c?.address].filter(Boolean).join(" - "),
            }),
        },
        {
            name: "path_id",
            label: t("ROUTE"),
            type: "select",
            optionKey: "transport_routes",
            options: (p) => ({
                value: p.id,
                label: getLocalizedText(p?.name_ar, p?.name_en) || p?.name,
            }),
        },
    ];

    const betweenCitiesLabelValues = [
        {
            key: "path",
            label: t("ROUTE"), // المسار
            render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
        },
        {
            key: "confirmation_id",
            label: t("BOARDING_ORDER"), // امر الاركاب
        },
        {
            key: "contract_ids",
            label: t("CONTRACT_NUMBER"),
            render: (values) => !!values?.length ?
                <Space>
                    <Info title={
                        <ul>
                            {values?.map((value) =>
                                <li>
                                    <SelectCachedOptions.Display
                                        value={value}
                                        optionKey="contracts"
                                        campareBy="id"
                                        render={(option) => [option?.id, option?.address].filter(Boolean).join(" - ")}
                                    />
                                </li>
                            )}
                        </ul>}
                    />
                    {values?.join(" - ")}
                </Space>
                : "-",
        },
        ...commonLabelValues,
    ];

    const betweenCitiesFilterFields = [
        {
            name: "tafweej_id",
            label: t("TDM_ID"),
            type: "input",
        },
        ...commonFilterFields,
    ]

    const fromPortsLabelValues = [
        {
            key: "path",
            label: t("ROUTE"), // المسار
            render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
        },
        {
            key: "flight_date",
            label: t("FLIGHT_DATE"),
            render: (_, arrival) =>
                arrival?.flight_detail?.flight_date
                    ? getLocalizedDate(
                        arrival?.flight_detail?.flight_date,
                        "DD - MMMM YYYY HH:mm"
                    )
                    : "-",
        },
        {
            key: "port",
            label: t("ENTRY_PORT"),
            render: (value) =>
                getLocalizedText(value?.name_ar, value?.name_en) || "-",
        },
        {
            key: "house_name_ar",
            label: t("HOUSING_CONTRACT"),
            render: (value, row) =>
                getLocalizedText(
                    value,
                    row?.house_name_en
                ) || "-",
        },
        ...commonLabelValues,
    ];

    const fromPortsFilterFields = [
        {
            name: "manifest_id",
            label: t("MANIFEST_ID"),
            type: "input",
        },
        ...commonFilterFields,
    ];

    const getLabelValues = () => {
        if (isFromPorts) {
            return fromPortsLabelValues;
        }
        if (isBetweenCities) {
            return betweenCitiesLabelValues;
        }
        return [];
    }
    const getFilterFields = () => {
        if (isFromPorts) {
            return fromPortsFilterFields;
        }
        if (isBetweenCities) {
            return betweenCitiesFilterFields;
        }
        return [];
    }

    const cardLabelValues = {
        title: isFromPorts ? `${t("MANIFEST_ID")}: ` : `${t("TDM_ID")}: `,
        titleValueKey: isFromPorts ? "manifest_id" : "tdm_id",
    }

    return {
        labelValues: getLabelValues(),
        setCurrentDate: (date) => setSearchParams(prev => ({ ...Object.fromEntries(prev), date })),
        currentDate,
        isFromPorts,
        isBetweenCities,
        currentSource,
        cardLabelValues,
        filterFields: getFilterFields(),
    }

}